<template>
  <div class="modal" :class="{ 'is-active': isActive }">
    <div class="modal-background" @click="handleClose"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Record Meeting</p>
        <button 
          class="delete" 
          aria-label="close"
          @click="handleClose"
        ></button>
      </header>
      <section class="modal-card-body">
        <div class="field">
          <div class="has-text-centered mb-4">
            <!-- Recording Status -->
            <p class="help mb-4" v-if="!isRecording && !recordedBlob">
              Click the button below to start recording your meeting
            </p>
            <p class="help mb-4 has-text-danger" v-if="isRecording">
              Recording in progress... ({{ recordingDuration }})
            </p>
            <p class="help mb-4 has-text-success" v-if="recordedBlob && !isUploading">
              Recording complete! Uploading will start automatically.
            </p>
            <p class="help mb-4 has-text-info" v-if="isUploading">
              Uploading recording... Please do not close this modal. It will automatically close when the upload is complete.
            </p>

            <!-- Record Button -->
            <button
              class="button is-primary is-large is-rounded"
              :class="{ 'is-danger': isRecording }"
              @click="toggleRecording"
              v-if="!recordedBlob"
            >
              <span class="">
                <i class="fas" :class="isRecording ? 'fa-stop' : 'fa-microphone'"></i>
              </span>
              <span>{{ isRecording ? 'Stop Recording' : 'Start Recording' }}</span>
            </button>

            <!-- Preview Section -->
            <div v-if="recordedBlob" class="mt-4">
              <video
                ref="preview"
                controls
                class="video-preview mb-4"
              ></video>

              <progress
                v-if="isUploading"
                class="progress is-primary"
                :value="percentComplete"
                max="100"
              >
                {{ percentComplete }}%
              </progress>

              <div class="buttons is-centered" v-else>
                <button
                  class="button"
                  @click="downloadRecording"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { awaitTask } from '../../checkup/helpers/tasks';
import { uploadToS3 } from '@frontend/shared/multipart-s3-uploads.js';

export default {
  name: 'RecordModal',
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    entityExternalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isRecording: false,
      recordedBlob: null,
      mediaRecorder: null,
      recordingChunks: [],
      isUploading: false,
      startTime: null,
      recordingDuration: '00:00',
      durationInterval: null,
      percentComplete: 0,
      uploadComplete: false,
      uploadFailed: false,
    };
  },
  computed: {
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
  },
  methods: {
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: true,
          video: false
        });
        
        this.mediaRecorder = new MediaRecorder(stream);
        this.recordingChunks = [];
        
        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            this.recordingChunks.push(e.data);
          }
        };
        
        this.mediaRecorder.onstop = () => {
          const blob = new Blob(this.recordingChunks, { type: 'video/mp4' });
          this.recordedBlob = blob;
          
          this.$nextTick(() => {
            if (this.$refs.preview) {
              const videoURL = URL.createObjectURL(blob);
              this.$refs.preview.src = videoURL;
            }
          });
          // Automatically upload once recording stops
          this.uploadRecording();
        };
        
        this.mediaRecorder.start();
        this.isRecording = true;
        this.startTime = Date.now();
        this.updateDuration();
        this.durationInterval = setInterval(this.updateDuration, 1000);
      } catch (error) {
        console.error('Error accessing media devices:', error);
        alert('Unable to access camera/microphone. Please ensure you have granted the necessary permissions.');
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
        this.isRecording = false;
        clearInterval(this.durationInterval);
      }
    },

    toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    },

    updateDuration() {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');
      this.recordingDuration = `${minutes}:${seconds}`;
    },

    discardRecording() {
      this.recordedBlob = null;
      this.recordingChunks = [];
      if (this.$refs.preview) {
        this.$refs.preview.src = '';
      }
    },

    async uploadRecording() {
      if (!this.recordedBlob) {
        alert('No recording to upload');
        return;
      }

      try {
        this.uploadFailed = false;
        const data = await this.prepareForUpload();

        if (!data.s3_key || !data.meeting_id) {
          throw new Error(`Failed to prepare upload. Response: ${JSON.stringify(data)}`);
        }

        this.isUploading = true;

        await uploadToS3(this.recordedBlob, data.s3_key, 5, status => {
          this.percentComplete = status.percentComplete;
        });


        // Start processing the meeting
        const created = await this.startProcessing(data.meeting_id);
        if (created) {
          this.$emit('meeting-created');
          this.$emit('update:isActive', false);
          this.discardRecording();
        }
        this.isUploading = false;
        this.uploadComplete = true;
      } catch (error) {
        console.error('Failed to upload recording:', error);
        this.isUploading = false;
        this.uploadFailed = true;
        alert('An error occurred while uploading the recording. Please try again.');
      }
    },

    async prepareForUpload() {
      const body = {
        file_name: 'recording.mp4',
        content_type: 'video/mp4',
      };

      if (this.entityExternalId) {
        body.entity_external_id = this.entityExternalId;
      }

      const res = await fetch('/api/v1/meetings/upload/prepare/', {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      });
      return res.json();
    },

    async startProcessing(id) {
      const res = await awaitTask(`/api/v1/meetings/${id}/process/`, {
        method: 'GET',
        headers: this.headers,
      });

      return res.task.status === 'complete';
    },

    handleClose() {
      if (this.isRecording) {
        this.stopRecording();
        return;
      }
      // Do not discard automatically; recordings are auto-saved on stop
      this.$emit('update:isActive', false);
    },

    downloadRecording() {
      if (this.recordedBlob) {
        const url = URL.createObjectURL(this.recordedBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recording.mp4';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    },
  },
  beforeDestroy() {
    if (this.isRecording) {
      this.stopRecording();
    }
    clearInterval(this.durationInterval);
  },
};
</script>

<style scoped>
.modal-card {
  max-width: 480px;
}

.video-preview {
  width: 100%;
  max-width: 400px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.button.is-rounded {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  flex-direction: column;
  padding: 2rem;
}

.button.is-rounded .icon {
  margin: 0 0 1rem 0;
  font-size: 2rem;
}
</style> 
